import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export type LegalType = 'privacy' | 'terms';
type Section = {
  heading: string;
  body: string;
};
const content: Record<LegalType, {
  title: string;
  sections: Section[];
}> = {
  privacy: {
    title: 'Política de Privacidad',
    sections: [{
      heading: '1. Información que recopilamos',
      body: 'Recopilamos la información que nos proporcionas directamente al contactarnos por WhatsApp o formularios, como tu nombre, número de teléfono, nombre del negocio, servicios que ofreces y cualquier material (fotos, textos) necesario para construir tu website.'
    }, {
      heading: '2. Cómo usamos tu información',
      body: 'Usamos tu información únicamente para crear, entregar y dar mantenimiento a tu website, comunicarnos contigo sobre tu proyecto y, si lo solicitas, gestionar tu perfil de Google Business. No vendemos ni rentamos tu información a terceros.'
    }, {
      heading: '3. Compartir información',
      body: 'Solo compartimos datos con proveedores estrictamente necesarios para operar tu sitio (por ejemplo, servicios de hosting o dominio). Estos proveedores están obligados a proteger tu información.'
    }, {
      heading: '4. Seguridad',
      body: 'Tomamos medidas razonables para proteger tu información. Sin embargo, ningún método de transmisión por internet es 100% seguro, por lo que no podemos garantizar seguridad absoluta.'
    }, {
      heading: '5. Tus derechos',
      body: 'Puedes solicitar acceso, corrección o eliminación de tu información en cualquier momento escribiéndonos por WhatsApp al +44 7311 754270.'
    }, {
      heading: '6. Cambios a esta política',
      body: 'Podemos actualizar esta política ocasionalmente. La versión más reciente siempre estará disponible en esta página.'
    }]
  },
  terms: {
    title: 'Términos de Servicio',
    sections: [{
      heading: '1. Servicio',
      body: 'AS Elite Contractor crea websites profesionales para contractors locales. El servicio inicia con un demo de tu website por $25. Si apruebas el demo, pagas los $225 restantes (total $250) y publicamos el sitio. Si decides no continuar, el único cargo es el demo de $25.'
    }, {
      heading: '2. Pagos',
      body: 'El pago de $25 por el demo no es reembolsable, ya que cubre el trabajo de diseño realizado. Los $225 restantes se cobran únicamente si apruebas el demo y decides lanzar tu website. Aceptamos pago por Zelle o tarjeta a través de Stripe. No hay mensualidades obligatorias.'
    }, {
      heading: '3. Tiempo de entrega',
      body: 'Entregamos tu website en menos de 72 horas a partir del momento en que recibimos toda la información necesaria de tu negocio (datos, fotos, servicios y contacto).'
    }, {
      heading: '4. Revisiones',
      body: 'Incluimos revisiones hasta que estés satisfecho con el diseño. No se requiere el pago final hasta que apruebes el resultado.'
    }, {
      heading: '5. Mantenimiento opcional',
      body: 'Ofrecemos un plan de mantenimiento opcional de $30/mes que incluye actualización de fotos, cambios de contenido y soporte. Es 100% opcional y puedes cancelarlo cuando quieras.'
    }, {
      heading: '6. Manejo de perfil de Google',
      body: 'El manejo del perfil de Google Business es un servicio aparte de $30/mes. El perfil debe ser creado por el cliente; nosotros únicamente lo gestionamos y optimizamos.'
    }, {
      heading: '7. Responsabilidades del cliente',
      body: 'El cliente es responsable de proporcionar información veraz y los materiales necesarios. El cliente es dueño del contenido que entrega y garantiza tener los derechos sobre el mismo.'
    }, {
      heading: '8. Contacto',
      body: 'Para cualquier duda sobre estos términos, escríbenos por WhatsApp al +44 7311 754270.'
    }]
  }
};
type Props = {
  type: LegalType | null;
  onClose: () => void;
};
export function LegalModal({
  type,
  onClose
}: Props) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (type) {
      document.addEventListener('keydown', handleKey);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [type, onClose]);
  const data = type ? content[type] : null;
  return <AnimatePresence data-id="element-206" data-mp="12y50a0e9oozk">
      {data && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} role="dialog" aria-modal="true" aria-label={data.title} data-id="element-207" data-mp="12x3n9qdoxsf7">
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.25,
        ease: 'easeOut'
      }} onClick={e => e.stopPropagation()} className="relative bg-dark-100 border border-white/10 rounded-3xl w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl" data-id="element-208" data-mp="3eavmkpo8on3f">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/10 shrink-0" data-id="element-209" data-mp="ewta7b8svae4">
              <h2 className="text-2xl font-bold text-white" data-id="element-210" data-mp="jvd01mqil6br">{data.title}</h2>
              <button onClick={onClose} aria-label="Cerrar" className="w-9 h-9 flex items-center justify-center rounded-full text-gray-400 hover:text-white hover:bg-white/10 transition-colors" data-id="element-211" data-mp="2fwya18g8f5if">
                <X className="w-5 h-5" data-id="element-212" data-mp="2ppcdq6qvem78" />
              </button>
            </div>

            {/* Body */}
            <div className="overflow-y-auto p-6 space-y-6" data-id="element-213" data-mp="2yo7hzdactm7w">
              {data.sections.map((section, i) => <div key={i} data-id="element-214" data-mp="2umvzdrpwe197" data-mpi={i}>
                  <h3 className="text-gold font-bold mb-2" data-id="element-215" data-mp="2ulkoz91y5v94">
                    {section.heading}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed" data-id="element-216" data-mp="38llyte06wd0q">
                    {section.body}
                  </p>
                </div>)}
              <p className="text-gray-600 text-xs pt-4 border-t border-white/5" data-id="element-217" data-mp="1e7kre4qkqhp1">
                Última actualización: 2024 · AS Elite Contractor
              </p>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}